import { Link } from "react-router-dom";
import type { RequestStatus } from "./types";

/* The two supplied lockups (public/brand, from scripts/build-brand-assets.mjs). The light one is
 * swapped for the dark one in dark mode; `tone="dark"` is for always-dark surfaces (the sign-in pane). */

const LOGO_RATIO = 760 / 136;

export function Logo({
  height = 28,
  to = "/",
  tone,
  onClick,
}: {
  height?: number;
  to?: string | null;
  tone?: "dark";
  onClick?: () => void;
}) {
  const width = Math.round(height * LOGO_RATIO);
  const img =
    tone === "dark" ? (
      <img src="/brand/logo-dark.png" alt="ConfirmPay" width={width} height={height} />
    ) : (
      <picture>
        <source media="(prefers-color-scheme: dark)" srcSet="/brand/logo-dark.png" />
        <img src="/brand/logo-light.png" alt="ConfirmPay" width={width} height={height} />
      </picture>
    );
  if (to === null) {
    return (
      <span className="logo" style={{ height }}>
        {img}
      </span>
    );
  }
  return (
    <Link to={to} className="logo" style={{ height }} aria-label="ConfirmPay home" onClick={onClick}>
      {img}
    </Link>
  );
}

/* Icons (stroke, currentColor) */

interface IconProps {
  size?: number;
  strokeWidth?: number;
}

function svgProps(size: number, strokeWidth: number) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    focusable: false,
  };
}

export function CheckIcon({ size = 16, strokeWidth = 2.4 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} style={{ flex: "none" }}>
      <path d="M3 8.5l3.2 3.2L13 4.5" />
    </svg>
  );
}
export function ClockIcon({ size = 16, strokeWidth = 2 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} style={{ flex: "none" }}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.8V8l2.2 1.4" />
    </svg>
  );
}
export function DashIcon({ size = 16, strokeWidth = 2.4 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} style={{ flex: "none" }}>
      <path d="M4 8h8" />
    </svg>
  );
}
export function CrossIcon({ size = 16, strokeWidth = 2.4 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} style={{ flex: "none" }}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}
export function PlusIcon({ size = 22, strokeWidth = 2.2 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} viewBox="0 0 22 22" className="plus">
      <path d="M11 4v14M4 11h14" />
    </svg>
  );
}
export function ArrowIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} viewBox="0 0 18 18">
      <path d="M3 9h12M10 4l5 5-5 5" />
    </svg>
  );
}
export function DownloadIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} viewBox="0 0 18 18">
      <path d="M9 2.5v9M5.5 8.5L9 12l3.5-3.5M3 14.5h12" />
    </svg>
  );
}
export function CloseIcon({ size = 16, strokeWidth = 2.2 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)}>
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </svg>
  );
}
export function ExternalIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg {...svgProps(size, strokeWidth)} viewBox="0 0 18 18">
      <path d="M5 13L13 5M6 5h7v7" />
    </svg>
  );
}

const STATUS = {
  confirmed: { cls: "paid", label: "Paid" },
  pending: { cls: "wait", label: "Waiting" },
  expired: { cls: "exp", label: "Expired" },
} as const;

export function StatusIcon({ status, size = 15 }: { status: RequestStatus; size?: number }) {
  const s = STATUS[status];
  return (
    <span className={`ico ico--${s.cls}`}>
      {status === "confirmed" ? (
        <CheckIcon size={size} />
      ) : status === "pending" ? (
        <ClockIcon size={size} />
      ) : (
        <DashIcon size={size} />
      )}
    </span>
  );
}

export function StatusChip({ status }: { status: RequestStatus }) {
  const s = STATUS[status];
  return <span className={`chip chip--${s.cls}`}>{s.label}</span>;
}

const PAYER_STEPS = ["Your details", "Scan and pay", "Paid"];

/** The three-step strip on the payer pages. `active` is the current step;
 *  `paid` turns the last segment sage once the payment has landed. */
export function PayerSteps({ active, paid = false }: { active: 1 | 2 | 3; paid?: boolean }) {
  return (
    <ol className="pbar" aria-label="Payment steps" style={{ margin: "0 0 30px" }}>
      {PAYER_STEPS.map((label, i) => {
        const n = i + 1;
        const seg = paid && n === 3 ? " pbar__seg--ok" : n <= active ? " pbar__seg--on" : "";
        return (
          <li key={label} aria-current={n === active ? "step" : undefined}>
            <div className={"pbar__seg" + seg} />
            <div className={"pbar__lab" + (n === active ? " pbar__lab--on" : "")}>{label}</div>
          </li>
        );
      })}
    </ol>
  );
}
